// pages/PhasesEditor.js
// Admin → Periods → (expanded period): staged release of the period's sections.
//
// A period with no phases has every section active. Add phases and every visible section must
// be in exactly one (the server enforces it for active periods and on activation); a phase with
// a release date keeps its sections locked — "Phase X releases on M/D/YY" — until 00:00 Central
// on that date, then unlocks by itself. Edits stay in a draft until Save (all-or-nothing, so
// moving a section between phases is one atomic change); the server validates and audits it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

let draftSeq = 0;

function nextName(phases) {
  const used = new Set(phases.map(p => p.name.trim().toLowerCase()));
  let n = phases.length + 1;
  while (used.has(`phase ${n}`)) n += 1;
  return `Phase ${n}`;
}

// 'YYYY-MM-DD' (Central today) → the day after, for the date input's min.
function dayAfter(iso) {
  if (!iso) return undefined;
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

function fmt(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
}

export default function PhasesEditor({ period, onError }) {
  const { reload: reloadAppConfig } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [today, setToday]     = useState(null);
  const [sections, setSections] = useState([]);   // [{ id, label, visible }]
  const [saved, setSaved]     = useState([]);     // phases as last loaded
  const [draft, setDraft]     = useState([]);     // [{ key, id?, name, releaseDate, sectionIds }]
  const [deleted, setDeleted] = useState([]);     // ids of saved phases removed in the draft
  const [open, setOpen]       = useState(null);   // draft key whose section picker is open

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr(null);
    try {
      const res = await fetch(`/api/diAdmin/period-phases/${period.id}`, { credentials: 'include' });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const j = await res.json();
      setToday(j.today);
      setSections(j.sections || []);
      setSaved(j.phases || []);
      setDraft((j.phases || []).map(p => ({ key: `p${p.id}`, id: p.id, name: p.name, releaseDate: p.releaseDate || '', sectionIds: [...p.sectionIds] })));
      setDeleted([]);
    } catch (e) { setLoadErr(e.message); }
    finally { setLoading(false); }
  }, [period.id]);

  useEffect(() => { load(); }, [load]);

  const savedById = useMemo(() => new Map(saved.map(p => [p.id, p])), [saved]);
  const phaseOfSection = useMemo(() => {
    const m = new Map();
    for (const p of draft) for (const sid of p.sectionIds) m.set(sid, p);
    return m;
  }, [draft]);
  const unassigned = sections.filter(s => s.visible && !phaseOfSection.has(s.id));

  const dirty = deleted.length > 0
    || draft.length !== saved.length
    || draft.some(p => {
      const o = p.id != null ? savedById.get(p.id) : null;
      if (!o) return true;
      return o.name !== p.name || (o.releaseDate || '') !== (p.releaseDate || '')
        || o.sectionIds.slice().sort().join(',') !== p.sectionIds.slice().sort().join(',');
    });

  const patch = (key, change) => setDraft(d => d.map(p => (p.key === key ? { ...p, ...change } : p)));

  function addPhase() {
    draftSeq += 1;
    setDraft(d => [...d, { key: `n${draftSeq}`, name: nextName(d), releaseDate: '', sectionIds: [] }]);
  }

  // Check a section into a phase (moving it out of any other — a section is in exactly one),
  // or uncheck it.
  function toggleSection(key, sid) {
    setDraft(d => d.map(p => {
      if (p.key === key) {
        return { ...p, sectionIds: p.sectionIds.includes(sid) ? p.sectionIds.filter(x => x !== sid) : [...p.sectionIds, sid] };
      }
      return { ...p, sectionIds: p.sectionIds.filter(x => x !== sid) };
    }));
  }

  function removePhase(p) {
    if (p.sectionIds.length) return;   // only an empty phase can be deleted
    setDraft(d => d.filter(x => x.key !== p.key));
    if (p.id != null) setDeleted(x => [...x, p.id]);
  }

  async function save() {
    setSaving(true);
    setSaveErr(null);
    try {
      const res = await fetch(`/api/diAdmin/period-phases/${period.id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phases: draft.map(p => ({ id: p.id, name: p.name, releaseDate: p.releaseDate || null, sectionIds: p.sectionIds })),
          deleteIds: deleted,
        }),
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      await load();
      reloadAppConfig({ silent: true });   // Home / section routes pick up the new locks at once
    } catch (e) {
      setSaveErr(e.message);
      onError && onError(e.message);
    } finally { setSaving(false); }
  }

  const minDate = dayAfter(today);

  return (
    <div className="admin-phases">
      <span className="admin-gate-label">Phases</span>
      <p className="admin-hint admin-phases-hint">
        No phases = every section is active. Add phases to release sections in stages: each visible section
        goes in exactly one phase, and a phase with a release date stays locked (“Phase 2 releases on 10/23/26”)
        until midnight Central on that date, then opens by itself. Leave the date blank to release a phase now.
        {period.is_active ? ' This period is active, so every visible section must be in a phase before you can save.' : ''}
      </p>

      {loading ? (
        <div className="admin-loading"><span className="admin-spinner" aria-hidden="true" /> Loading phases…</div>
      ) : loadErr ? (
        <div className="admin-phases-err">Couldn’t load phases: {loadErr}</div>
      ) : (
        <>
          {draft.length === 0 && <div className="admin-phases-empty">No phases — every section is active.</div>}

          {draft.map(p => {
            const orig = p.id != null ? savedById.get(p.id) : null;
            const released = orig ? !orig.locked : false;
            const picking = open === p.key;
            return (
              <div key={p.key} className="admin-phase">
                <div className="admin-phase-row">
                  <input className="admin-input admin-phase-name" value={p.name} maxLength={100}
                    aria-label="Phase name" onChange={e => patch(p.key, { name: e.target.value })} />
                  <input className="admin-input admin-phase-date" type="date" value={p.releaseDate}
                    min={minDate} aria-label="Release date"
                    title="Release date (blank = released now). Must be after today."
                    onChange={e => patch(p.key, { releaseDate: e.target.value })} />
                  {orig && (
                    <span className={`admin-phase-status ${released ? 'released' : 'locked'}`}>
                      {released ? 'Released' : `Locked until ${fmt(orig.releaseDate)}`}
                    </span>
                  )}
                  <button type="button" className="admin-btn admin-btn--secondary"
                    onClick={() => setOpen(picking ? null : p.key)}>
                    {p.sectionIds.length} section{p.sectionIds.length === 1 ? '' : 's'} {picking ? '▾' : '▸'}
                  </button>
                  <button type="button" className="admin-btn admin-btn--secondary"
                    disabled={p.sectionIds.length > 0}
                    title={p.sectionIds.length ? 'Move this phase’s sections to another phase first.' : 'Delete this phase'}
                    onClick={() => removePhase(p)}>
                    Delete
                  </button>
                </div>
                {picking && (
                  <div className="admin-phase-sections">
                    {sections.map(s => {
                      const owner = phaseOfSection.get(s.id);
                      const here = owner && owner.key === p.key;
                      return (
                        <label key={s.id} className={`admin-phase-section ${owner && !here ? 'elsewhere' : ''}`}>
                          <input type="checkbox" checked={!!here} onChange={() => toggleSection(p.key, s.id)} />
                          {s.label}
                          {!s.visible && <em> (hidden)</em>}
                          {owner && !here && <em> — in {owner.name}; checking moves it here</em>}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {draft.length > 0 && unassigned.length > 0 && (
            <div className={period.is_active ? 'admin-phases-err' : 'admin-phases-warn'}>
              Not in a phase: {unassigned.map(s => s.label).join(', ')}.
              {period.is_active ? ' Required before saving.' : ' Required before this period can be activated.'}
            </div>
          )}
          {saveErr && <div className="admin-phases-err">{saveErr}</div>}

          <div className="admin-phases-actions">
            <button type="button" className="admin-btn admin-btn--secondary" onClick={addPhase}>+ Add phase</button>
            <button type="button" className="admin-btn" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save phases'}
            </button>
            {dirty && !saving && (
              <button type="button" className="admin-btn admin-btn--secondary" onClick={load}>Discard changes</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
